/* ==========================================================================
   Cart drawer behaviour: cross-sell adds, the bundle upsell, the shipping
   protection switch, and the free-shipping progress animation.

   Everything is delegated or observed from the document, because theme.js
   refreshes the drawer by replacing its innerHTML — handlers bound to the old
   nodes would die on the first refresh.
   ========================================================================== */
(function () {
  'use strict';

  const host = document.querySelector('cart-drawer');
  if (!host) return;

  const busy = (state) => {
    const body = document.querySelector('[data-cart-body]');
    if (body) body.classList.toggle('is-busy', state);
  };

  /* ---------------------------------------------------------------- cart ops */
  async function addVariant(id, quantity) {
    busy(true);
    try {
      await window.HM.Cart.add([{ id: Number(id), quantity: Number(quantity) || 1 }]);
      await window.HM.Cart.refresh();
    } catch (error) {
      window.HM.toast(error.message);
    } finally {
      busy(false);
    }
  }

  async function removeLine(index) {
    busy(true);
    try {
      await window.HM.Cart.change(Number(index), 0);
      await window.HM.Cart.refresh();
    } finally {
      busy(false);
    }
  }

  async function addVariants(ids) {
    busy(true);
    try {
      const items = ids.map((id) => ({ id: Number(id), quantity: 1 }));
      await window.HM.Cart.add(items);
      await window.HM.Cart.refresh();
    } catch (error) {
      window.HM.toast(error.message);
    } finally {
      busy(false);
    }
  }

  document.addEventListener('click', function (event) {
    /* The pair upsell: one tap, two products, one request. */
    const many = event.target.closest('[data-cd-add-many]');
    if (many && !many.disabled) {
      event.preventDefault();
      addVariants(many.dataset.cdAddMany.split(',').filter(Boolean));
      return;
    }

    const add = event.target.closest('[data-cd-add]');
    if (add && !add.disabled) {
      event.preventDefault();
      addVariant(add.dataset.cdAdd, add.dataset.cdQty);
      return;
    }

    /* the same control adds or removes the protection fee line */
    const protect = event.target.closest('[data-cd-protect]');
    if (protect) {
      event.preventDefault();
      const line = protect.dataset.cdProtectLine;
      if (line) removeLine(line);
      else addVariant(protect.dataset.cdProtect, 1);
    }
  });

  /* -------------------------------------------------- free-shipping progress */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let wasFree = null;
  let celebrateTimer;

  const shipEl = () => host.querySelector('[data-cd-ship]');

  function celebrate(el) {
    if (reduceMotion) return;
    el.classList.remove('is-celebrating');
    void el.offsetWidth; // restart the ring and the sparks
    el.classList.add('is-celebrating');
    clearTimeout(celebrateTimer);
    celebrateTimer = setTimeout(() => el.classList.remove('is-celebrating'), 1100);
  }

  /* The bar is rendered empty and driven to its real width here, so the fill
     replays every time the drawer opens. `replay` forces the celebration even
     when the cart already qualified before opening — the case where someone
     adds a product from the PDP and the drawer opens already past the line. */
  function paintShip(replay) {
    const el = shipEl();
    if (!el) return;

    const fill = el.querySelector('.cd__bar-fill');
    const pct = parseFloat(el.dataset.pct || '0');
    const free = el.dataset.free === 'true';

    if (fill) {
      /* Commit the 0 with a forced reflow rather than waiting for a frame. On a
         refresh the node is brand new and a rAF-deferred set was measured still
         sitting at 0 — the callback landed on a node the next observer pass had
         already replaced. Synchronous is both simpler and reliable. */
      fill.style.width = '0%';
      void fill.offsetWidth;
      fill.style.width = pct + '%';
    }

    if (free && (replay || wasFree === false)) celebrate(el);
    wasFree = free;
  }

  /* ------------------------------------------------- Shopify recommendations
     Both suggestion blocks come from the Product Recommendations API rather
     than a hand-picked list, so they follow the catalogue as it grows. The
     cross-sell asks for complementary products first — those are the ones a
     merchant has actually paired up in Search & Discovery — and falls back to
     related, which Shopify derives on its own and always returns something. */
  const panelEl = () => host.querySelector('[data-drawer-panel]');

  async function fetchSection(sectionId, params) {
    const panel = panelEl();
    if (!panel) return '';
    const base = panel.dataset.recommendUrl;
    const seed = panel.dataset.seed;
    if (!base || !seed) return '';

    const url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', seed);
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));

    try {
      const response = await fetch(url.toString());
      if (!response.ok) return '';
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const body = doc.querySelector('[data-cross-list], [data-bundle]');
      return body ? body.outerHTML : '';
    } catch (error) {
      return '';
    }
  }

  async function loadCross() {
    const mount = host.querySelector('[data-cross-mount]');
    const group = host.querySelector('[data-cross-group]');
    if (!mount) return;

    const limit = Number((panelEl() && panelEl().dataset.crossLimit) || 3);
    let html = await fetchSection('cart-cross-sell', { limit: 10, intent: 'complementary' });
    if (!html) html = await fetchSection('cart-cross-sell', { limit: 10, intent: 'related' });

    mount.innerHTML = html;

    /* The section ranks; the merchant decides how many of the ranked survive. */
    const rows = mount.querySelectorAll('.cd__cross-row');
    for (let i = limit; i < rows.length; i += 1) rows[i].remove();

    if (group) group.hidden = !mount.querySelector('.cd__cross-row');
  }

  async function loadBundle() {
    const mount = host.querySelector('[data-bundle-mount]');
    const card = host.querySelector('[data-bundle-card]');
    if (!mount) return;

    /* Ask wide: the ranking discards anything owned, out of stock or dearer
       than the anchor, so a small pool often ranks down to nothing. */
    const html = await fetchSection('cart-bundle', { limit: 10, intent: 'related' });
    mount.innerHTML = html;
    if (card) card.hidden = !html;
  }

  /* Both lists come from the same recommendation pool, so the pair and the
     cross-sell can name the same product. The pair is the stronger offer, so it
     wins and the duplicate row below is dropped. */
  function dedupe() {
    const pairBtn = host.querySelector('[data-cd-add-many]');
    if (!pairBtn) return;
    const taken = pairBtn.dataset.cdAddMany.split(',');
    host.querySelectorAll('[data-cross-list] [data-cd-add]').forEach((btn) => {
      if (taken.indexOf(btn.dataset.cdAdd) === -1) return;
      const row = btn.closest('.cd__cross-row');
      if (row) row.remove();
    });

    const list = host.querySelector('[data-cross-list]');
    const group = host.querySelector('[data-cross-group]');
    if (group && list && !list.querySelector('.cd__cross-row')) group.hidden = true;
  }

  async function loadSuggestions() {
    await Promise.all([loadBundle(), loadCross()]);
    dedupe();
  }

  /* ------------------------------------------------------------- open / sync */
  let isOpen = false;

  const initial = shipEl();
  if (initial) wasFree = initial.dataset.free === 'true';

  new MutationObserver(function (records) {
    // ignore the class flips this script makes on the progress block itself
    const ours = records.every((r) => r.target.hasAttribute && r.target.hasAttribute('data-cd-ship'));
    if (ours) return;

    const panel = host.querySelector('[data-drawer-panel]');
    if (!panel) return;

    /* A refresh replaces the drawer's innerHTML, and the panel that carries
       .is-open lives inside it — so the drawer would snap shut after every add
       or remove. Re-apply the open state to whatever panel replaced it. */
    const overlay = document.querySelector('.drawer-overlay');
    if (overlay && overlay.classList.contains('is-open') && !panel.classList.contains('is-open')) {
      panel.classList.add('is-open');
    }

    const open = panel.classList.contains('is-open');
    const refreshed = records.some((r) => r.type === 'childList' && r.target === host);

    if (open && !isOpen) {
      isOpen = true;
      paintShip(true);
      loadSuggestions();
    } else if (!open) {
      isOpen = false;
    } else if (refreshed) {
      paintShip(false);
      /* the seed moves with the basket, so the suggestions are re-asked for */
      loadSuggestions();
    }
  }).observe(host, { childList: true, subtree: true, attributeFilter: ['class'] });
})();
